import { ArticleWithTechnicalPropertiesDTO, ArticleWithTechnicalValuesUtils, isNumberValue, isStringValue } from '@article/properties/technical/technical-requirements';
import { definedValues, numeric, numericBy, unique, uniqueBy } from '@shared/util/array-utils';

export class ArticlesWithTechnicalValues<T extends string> {

  private readonly _articles: readonly ArticleWithTechnicalPropertiesDTO[];
  public get articles() {
    return this._articles;
  }

  constructor(articles: ArticleWithTechnicalPropertiesDTO[]) {
    this._articles = ArticleWithTechnicalValuesUtils.sort(articles);
  }

  public filterByPropertyValue(propertyName: T, value: any): ArticlesWithTechnicalValues<T> {
    const filteredArticles = this.articles.filter((article) => ArticleWithTechnicalValuesUtils.match(article, propertyName, value));
    return new ArticlesWithTechnicalValues<T>(
      filteredArticles,
    );
  }

  public getStringValues(...propertyNames: T[]): string[] {
    return this.articles
      .flatMap((article) => propertyNames.map((propertyName) => article.properties[propertyName]))
      .filter(definedValues)
      .filter(isStringValue)
      .map((property) => property.value)
      .filter(unique)
      .sort(numeric);
  }

  public getStringWithEnglishValues(propertyName: T): { value: string, englishValue: string }[] {
    return this.articles
      .map((article) => article.properties[propertyName])
      .filter(definedValues)
      .filter(isStringValue)
      .map((property) => ({value: property.value, englishValue: property.englishValue}))
      .filter(uniqueBy(_ => _.value))
      .sort(numericBy(_ => _.value));
  }

  public getNumberValues(propertyName: T): number[] {
    return this.articles
      .map((article) => article.properties[propertyName])
      .filter(definedValues)
      .filter(isNumberValue)
      .map((property) => property.value)
      .filter(unique)
      .sort((n1, n2) => n1 - n2);
  }

  public getValuesWithUnit(propertyName: T): string[] {
    return this.articles
      .map((article) => article.properties[propertyName])
      .filter(definedValues)
      .map((property) => `${ property.value } ${ property.unit }`)
      .filter(unique)
      .sort(numeric);
  }

  public findByPropertyValues(propertyValues: { propertyNames: T | T[], value: any }[]): ArticleWithTechnicalPropertiesDTO | undefined {
    return this.articles.find((article) => {
      return propertyValues.every(({propertyNames, value}) => {
        const names = Array.isArray(propertyNames) ? propertyNames : [ propertyNames ];
        return names.some((propertyName) => ArticleWithTechnicalValuesUtils.match(article, propertyName, value));
      });
    });
  }

  public find(predicate: (article: ArticleWithTechnicalPropertiesDTO) => boolean): ArticleWithTechnicalPropertiesDTO | undefined {
    return this.articles.find(predicate);
  }

  public filter(predicate: (article: ArticleWithTechnicalPropertiesDTO) => boolean): ArticleWithTechnicalPropertiesDTO[] {
    return this.articles.filter(predicate);
  }
}
